import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Grid,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Tabs,
  Tag,
  Space,
} from 'antd'
import { DeleteOutlined, FilterOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { Toast } from '@/components/Toast'
import { ID } from '@/types/common'
import { ErrorCode, isAppError } from '@/utils/error'
import classRecordApi from './api'
import { createRecordColumns } from './columns'
import { idKey } from './helpers'
import { defaultClassRecordRules, emptySummary, getMonthRange } from './page-types'
import { useClassRecordConfigDraft } from './hooks/useClassRecordConfigDraft'
import { useClassRecordTableMeta } from './hooks/useClassRecordTableMeta'
import { useClassRecordData } from './hooks/useClassRecordData'
import { useClassRecordBatchEdit } from './hooks/useClassRecordBatchEdit'
import { useClassRecordRecordActions } from './hooks/useClassRecordRecordActions'
import { useClassRecordQuickCreate } from './hooks/useClassRecordQuickCreate'
import { useClassRecordVersionManagement } from './hooks/useClassRecordVersionManagement'
import { ClassConfigDrawer } from './components/ClassConfigDrawer'
import { ConfigDetailSections } from './components/ConfigDetailSections'
import { MonthPlanDrawer } from './components/MonthPlanDrawer'
import { ClassRecordKind } from './types'

const { Option } = Select
const { RangePicker } = DatePicker
const { useBreakpoint } = Grid

const BatchEditModal = lazy(() =>
  import('./components/BatchEditModal').then((module) => ({
    default: module.BatchEditModal,
  })),
)
const QuickCreateModal = lazy(() =>
  import('./components/QuickCreateModal').then((module) => ({
    default: module.QuickCreateModal,
  })),
)
const GenerateRecordsDrawer = lazy(() =>
  import('./components/GenerateRecordsDrawer').then((module) => ({
    default: module.GenerateRecordsDrawer,
  })),
)

const ClassRecordsPage = () => {
  const [selectedStudentId, setSelectedStudentId] = useState<ID | undefined>()
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(() =>
    dayjs().startOf('month'),
  )
  const [selectedDateRange, setSelectedDateRange] = useState<
    [Dayjs, Dayjs] | null
  >(() => getMonthRange(dayjs()))
  const [activeTab, setActiveTab] = useState<'query' | 'versions'>('query')

  const [selectedRecordTypeFilter, setSelectedRecordTypeFilter] = useState<
    ID | 'all' | 'manual' | 'makeup'
  >('all')
  const [generateRange, setGenerateRange] = useState<[Dayjs, Dayjs] | null>(
    null,
  )
  const [generating, setGenerating] = useState(false)
  const [savingCurrentConfig, setSavingCurrentConfig] = useState(false)
  const [generateDrawerOpen, setGenerateDrawerOpen] = useState(false)
  const [classConfigDrawerOpen, setClassConfigDrawerOpen] = useState(false)
  const [globalConfigDrawerOpen, setGlobalConfigDrawerOpen] = useState(false)
  const [monthPlanDrawerOpen, setMonthPlanDrawerOpen] = useState(false)
  const [ruleSaving, setRuleSaving] = useState(false)
  const [ruleDraft, setRuleDraft] = useState(defaultClassRecordRules())

  const [batchSelectedIds, setBatchSelectedIds] = useState<ID[]>([])
  const [batchDeleteSaving, setBatchDeleteSaving] = useState(false)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined)

  const screens = useBreakpoint()
  const monthText = selectedMonth?.format('YYYY-MM') ?? ''
  const batchActionCount = batchSelectedIds.length
  const {
    students,
    teachers,
    studentsLoading,
    teachersLoading,
    records,
    setRecords,
    summary,
    rules,
    setRules,
    currentConfig,
    generationBatches,
    configVersions,
    loading,
    setLoading,
    hasQueried,
    setHasQueried,
    recordsReloadVersion,
    monthPlan,
    loadStudentConfig,
    loadRecords,
  } = useClassRecordData({
    selectedStudentId,
    monthText,
    selectedDateRange,
    activeTab,
  })
  const filteredRecords = useMemo(() => {
    if (selectedRecordTypeFilter === 'all') {
      return records
    }
    if (selectedRecordTypeFilter === 'manual') {
      return records.filter((record) => record.recordKind === ClassRecordKind.MANUAL)
    }
    if (selectedRecordTypeFilter === 'makeup') {
      return records.filter(
        (record) => record.recordKind === ClassRecordKind.MAKEUP_USAGE,
      )
    }
    return records.filter(
      (record) => record.generationBatchId === selectedRecordTypeFilter,
    )
  }, [records, selectedRecordTypeFilter])

  const {
    rangePresets,
    dailySequenceMap,
    dateGroupMeta,
    unifiedMonthlyPriceByDate,
  } = useClassRecordTableMeta(filteredRecords, selectedMonth)
  const {
    configDraftWeekdays,
    setConfigDraftWeekdays,
    configDraftIsAllDayPricing,
    setConfigDraftIsAllDayPricing,
    configDraftMonthlyPrice,
    setConfigDraftMonthlyPrice,
    configDraftTemplates,
    getDraftValidationMessage,
    isConfigDirty,
    buildConfigTemplatePayload,
    handleConfigDraftTemplateChange,
    handleConfigDraftAddTemplate,
    handleConfigDraftRemoveTemplate,
    hydrateFromConfig,
    resetConfigDraft,
  } = useClassRecordConfigDraft(currentConfig, rules)
  const {
    batchEditModalOpen,
    setBatchEditModalOpen,
    batchEditAttendanceMode,
    setBatchEditAttendanceMode,
    batchEditTeacherValue,
    setBatchEditTeacherValue,
    batchEditNote,
    setBatchEditNote,
    batchEditSaving,
    handleOpenBatchEdit,
    handleSubmitBatchEdit,
  } = useClassRecordBatchEdit({
    selectedStudentId,
    monthText,
    batchSelectedIds,
    setBatchSelectedIds,
    loadRecords,
  })
  const {
    noteDraftMap,
    setNoteDraftMap,
    noteSavingMap,
    handleNoteBlur,
    resetRecordActionDrafts,
  } = useClassRecordRecordActions({
    setRecords,
  })
  const {
    quickCreateModalOpen,
    setQuickCreateModalOpen,
    quickCreateDate,
    setQuickCreateDate,
    quickCreateItems,
    quickCreateRecordKind,
    quickCreateSaving,
    handleOpenQuickCreateModal,
    handleQuickCreateRecordKindChange,
    handleQuickCreateItemChange,
    handleQuickCreateAddItem,
    handleQuickCreateDuplicateItem,
    handleQuickCreateRemoveItem,
    handleSubmitQuickCreate,
  } = useClassRecordQuickCreate({
    currentConfig,
    rules,
    selectedStudentId,
    loadRecords,
  })
  const {
    selectedVersionBatchDrawerId,
    setSelectedVersionBatchDrawerId,
    selectedVersionBatchDrawer,
    deletingVersionId,
    handleDeleteConfigVersion,
    handleSetCurrentVersion,
    generationBatchMonthlyPriceMap,
    versionBatchColumns,
  } = useClassRecordVersionManagement({
    configVersions,
    generationBatches,
    selectedStudentId,
    loadStudentConfig,
    loadRecords,
  })

  useEffect(() => {
    if (!rules) {
      return
    }
    setRuleDraft(rules)
  }, [rules])

  useEffect(() => {
    setSelectedVersionBatchDrawerId(null)
    resetConfigDraft()
  }, [resetConfigDraft, selectedStudentId, setSelectedVersionBatchDrawerId])

  useEffect(() => {
    setBatchSelectedIds([])
    resetRecordActionDrafts()
  }, [recordsReloadVersion, resetRecordActionDrafts])

  useEffect(() => {
    if (!selectedStudentId || !currentConfig) {
      return
    }
    if (currentConfig.studentId !== selectedStudentId) {
      return
    }
    hydrateFromConfig(currentConfig)
  }, [currentConfig, hydrateFromConfig, selectedStudentId])

  const handleClearCurrentMonth = () => {
    if (!selectedStudentId || !monthText) {
      Toast.warning('请先选择学生和月份')
      return
    }
    Modal.confirm({
      title: '确认清空',
      content: `将清空该学生 ${monthText} 的所有上课记录和生成批次，是否继续？`,
      okText: '确认清空',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setLoading(true)
          const res = await classRecordApi.clearMonth({
            studentId: selectedStudentId,
            month: monthText,
          })
          Toast.success(res?.message || '当月数据已清空')
          await Promise.all([
            loadRecords(),
            loadStudentConfig(selectedStudentId),
          ])
        } catch (error) {
          Toast.error('清空当月数据失败')
          console.error('清空当月数据失败:', error)
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleMonthChange = (value: Dayjs | null) => {
    setHasQueried(false)
    setSelectedRecordTypeFilter('all')
    setSelectedMonth(value)
    if (!value) {
      setSelectedDateRange(null)
      setGenerateRange(null)
      return
    }
    const monthRange = getMonthRange(value)
    setSelectedDateRange(monthRange)
    setGenerateRange(monthRange)
  }

  const handleGenerate = async (force = false) => {
    if (!selectedStudentId || !monthText) {
      Toast.warning('请先选择学生和月份')
      return
    }
    if (!currentConfig) {
      Toast.warning('暂无当前配置，请先配置后再生成')
      return
    }
    if (!generateRange?.[0] || !generateRange?.[1]) {
      Toast.warning('请先选择生成日期范围')
      return
    }

    try {
      setGenerating(true)
      const res = await classRecordApi.generate({
        studentId: selectedStudentId,
        startDate: generateRange[0].format('YYYY-MM-DD'),
        endDate: generateRange[1].format('YYYY-MM-DD'),
        force,
      })
      Toast.success(
        res?.message || (isConfigDirty ? '生成成功，已同步更新为学生当前配置' : '生成成功'),
      )
      setGenerateDrawerOpen(false)
      await Promise.all([loadRecords(), loadStudentConfig(selectedStudentId)])
    } catch (error) {
      if (!force && isAppError(error) && error.code === ErrorCode.CONFLICT) {
        if (error.message.includes('自动生成记录与当前区间重叠')) {
          Modal.confirm({
            title: '检测到重叠生成批次',
            content: <div className="whitespace-pre-wrap">{error.message}</div>,
            okText: '确认覆盖',
            cancelText: '取消',
            okButtonProps: { danger: true },
            onOk: async () => {
              await handleGenerate(true)
            },
          })
        } else {
          Toast.warning(error.message)
        }
        return
      }
      Toast.error('生成失败')
      console.error('生成失败:', error)
    } finally {
      setGenerating(false)
    }
  }

  const handleSaveCurrentConfig = async () => {
    if (!selectedStudentId) {
      Toast.warning('请先选择学生')
      return
    }
    const validationMessage = getDraftValidationMessage(configDraftTemplates)
    if (validationMessage) {
      Toast.warning(validationMessage)
      return
    }

    try {
      setSavingCurrentConfig(true)
      const res = await classRecordApi.upsertCurrentConfig({
        studentId: selectedStudentId,
        weekdays: configDraftWeekdays,
        templates: buildConfigTemplatePayload(),
        isAllDayPricing: configDraftIsAllDayPricing,
        monthlyPrice: configDraftIsAllDayPricing
          ? configDraftMonthlyPrice
          : undefined,
      })
      Toast.success(res?.message || '保存成功，未生成签到记录')
      setGenerateDrawerOpen(false)
      setClassConfigDrawerOpen(false)
      await loadStudentConfig(selectedStudentId)
    } catch (error) {
      Toast.error('保存失败')
      console.error('保存失败:', error)
    } finally {
      setSavingCurrentConfig(false)
    }
  }

  const handleSaveRules = async () => {
    try {
      setRuleSaving(true)
      const res = await classRecordApi.updateRules({
        makeupRetentionMonths: ruleDraft.makeupRetentionMonths,
        defaultCoursePrice: ruleDraft.defaultCoursePrice,
        defaultClassDurationMinutes: ruleDraft.defaultClassDurationMinutes,
        note: ruleDraft.note ?? undefined,
      })
      const latestRules = await classRecordApi.getRules()
      setRules(latestRules)
      setRuleDraft(latestRules)
      Toast.success(res?.message || '规则保存成功')
      setGlobalConfigDrawerOpen(false)
      if (selectedStudentId && monthText) {
        await loadRecords()
      }
    } catch (error) {
      Toast.error('保存规则失败')
      console.error('保存规则失败:', error)
    } finally {
      setRuleSaving(false)
    }
  }

  const handleOpenGenerateDrawer = () => {
    if (!showGeneratePanel) {
      return
    }
    setClassConfigDrawerOpen(false)
    setGenerateDrawerOpen(true)
  }

  const handleOpenClassConfigDrawer = () => {
    if (!selectedStudentId) {
      Toast.warning('请先选择学生')
      return
    }
    hydrateFromConfig(currentConfig)
    setGenerateDrawerOpen(false)
    setClassConfigDrawerOpen(true)
  }

  const columns = useMemo(
    () =>
      createRecordColumns({
        currentConfigMonthlyPrice: currentConfig?.monthlyPrice,
        dailySequenceMap,
        dateGroupMeta,
        generationBatchMonthlyPriceMap,
        noteDraftMap,
        noteSavingMap,
        setNoteDraftMap,
        unifiedMonthlyPriceByDate,
        onNoteBlur: handleNoteBlur,
      }),
    [
      currentConfig?.monthlyPrice,
      dailySequenceMap,
      dateGroupMeta,
      generationBatchMonthlyPriceMap,
      noteDraftMap,
      noteSavingMap,
      setNoteDraftMap,
      unifiedMonthlyPriceByDate,
      handleNoteBlur,
    ],
  )

  const summaryData = summary || emptySummary(selectedStudentId, monthText)

  const batchFilterOptions = useMemo(() => {
    if (!monthText) {
      return [] as Array<{ id: ID; label: string }>
    }
    return generationBatches
      .filter((item) => item.generatedMonth === monthText)
      .map((item) => ({
        id: item.id,
        label: `批量生成 · ${dayjs(item.createdAt).format('MM-DD HH:mm')} · ${item.generatedStartDate.slice(5)}~${item.generatedEndDate.slice(5)}`,
      }))
  }, [generationBatches, monthText])

  const selectedRecords = useMemo(() => {
    const selectedKeySet = new Set(batchSelectedIds.map((id) => idKey(id)))
    return filteredRecords.filter((record) =>
      selectedKeySet.has(idKey(record.id)),
    )
  }, [batchSelectedIds, filteredRecords])

  const selectedManualIds = useMemo(
    () =>
      selectedRecords
        .filter(
          (record) => record.recordKind !== ClassRecordKind.PLANNED,
        )
        .map((record) => record.id),
    [selectedRecords],
  )
  const selectedGeneratedCount = selectedRecords.length - selectedManualIds.length

  const showGeneratePanel = Boolean(selectedStudentId) && Boolean(monthText)
  const showRecordsTable = hasQueried && (loading || filteredRecords.length > 0)
  const canClearMonth = Boolean(selectedStudentId && monthText && !loading)
  const canQuickCreate = Boolean(
    selectedStudentId && monthText && currentConfig && !loading,
  )
  const canBatchEdit = Boolean(
    selectedStudentId &&
      monthText &&
      !loading &&
      !batchDeleteSaving &&
      !batchEditSaving &&
      batchSelectedIds.length > 0,
  )
  const canBatchDelete = Boolean(
    selectedStudentId &&
      monthText &&
      !loading &&
      !batchEditSaving &&
      !batchDeleteSaving &&
      batchSelectedIds.length > 0,
  )

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: batchSelectedIds,
      onChange: (selectedRowKeys: Array<string | number | bigint>) => {
        setBatchSelectedIds(selectedRowKeys as ID[])
      },
    }),
    [batchSelectedIds],
  )

  const handleToggleRowSelection = useCallback(
    (recordId: ID) => {
      setBatchSelectedIds(
        batchSelectedIds.some((selectedId) => idKey(selectedId) === idKey(recordId))
          ? batchSelectedIds.filter((selectedId) => idKey(selectedId) !== idKey(recordId))
          : [...batchSelectedIds, recordId],
      )
    },
    [batchSelectedIds, setBatchSelectedIds],
  )

  useEffect(() => {
    setBatchSelectedIds([])
  }, [selectedRecordTypeFilter])

  const updateTableScrollY = useCallback(() => {
    if (!screens.md) {
      setTableScrollY(undefined)
      return
    }
    const container = tableContainerRef.current
    if (!container) {
      return
    }

    const summaryBar = container.firstElementChild as HTMLElement | null
    const summaryBarHeight = summaryBar?.getBoundingClientRect().height ?? 0
    const tableHeaderHeight =
      container.querySelector('.ant-table-thead')?.getBoundingClientRect()
        .height ?? 56
    const containerHeight = container.getBoundingClientRect().height

    const next = Math.max(
      180,
      Math.floor(containerHeight - summaryBarHeight - tableHeaderHeight - 24),
    )
    setTableScrollY(next)
  }, [screens.md])

  useEffect(() => {
    const rafId = window.requestAnimationFrame(updateTableScrollY)
    window.addEventListener('resize', updateTableScrollY)
    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateTableScrollY)
    }
  }, [updateTableScrollY])

  useEffect(() => {
    const rafId = window.requestAnimationFrame(updateTableScrollY)
    return () => window.cancelAnimationFrame(rafId)
  }, [updateTableScrollY, showRecordsTable, records.length, loading])

  const handleBatchDelete = useCallback(() => {
    if (!selectedStudentId || !monthText) {
      Toast.warning('请先选择学生和月份并查询')
      return
    }
    if (batchSelectedIds.length === 0) {
      Toast.warning('请先勾选要删除的记录')
      return
    }
    if (selectedManualIds.length === 0) {
      Toast.warning('当前未选中手工记录或补课消耗记录')
      return
    }
    if (selectedGeneratedCount > 0) {
      Toast.warning('批量删除仅支持非批量生成记录，请取消自动生成记录后再试')
      return
    }

    Modal.confirm({
      title: '确认批量删除',
      content: `将删除 ${selectedManualIds.length} 条手工记录，删除后不可恢复，是否继续？`,
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setBatchDeleteSaving(true)
          const res = await classRecordApi.batchDelete(selectedManualIds)
          Toast.success(res?.message || `已删除 ${selectedManualIds.length} 条手工记录`)
          setBatchSelectedIds([])
          await loadRecords()
        } catch (error) {
          Toast.error('批量删除手工记录失败')
          console.error('批量删除手工记录失败:', error)
        } finally {
          setBatchDeleteSaving(false)
        }
      },
    })
  }, [
    batchSelectedIds.length,
    loadRecords,
    monthText,
    selectedGeneratedCount,
    selectedManualIds,
    selectedStudentId,
  ])

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-slate-50/60 p-3 md:p-6">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[28px] border-0 shadow-[0_22px_60px_-34px_rgba(15,23,42,0.35)] [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
        <Tabs
          className="flex min-h-0 flex-1 flex-col [&_.ant-tabs-content]:h-full [&_.ant-tabs-content]:min-h-0 [&_.ant-tabs-content-holder]:flex-1 [&_.ant-tabs-content-holder]:min-h-0 [&_.ant-tabs-nav]:mb-5 [&_.ant-tabs-tabpane]:h-full [&_.ant-tabs-tabpane]:min-h-0"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'query' | 'versions')}
          items={[
            {
              key: 'query',
              label: '上课记录',
              children: (
                <>
                  <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pr-1">
                    <div className="rounded-xl border border-slate-200/80 bg-slate-50/75 p-4 shadow-[0_16px_35px_-30px_rgba(15,23,42,0.24)]">
                      <div className="grid gap-3 xl:grid-cols-4">
                        <div>
                          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                            <FilterOutlined className="text-slate-500" />
                            <span>学生</span>
                          </div>
                          <Select
                            placeholder="请选择学生"
                            style={{ width: '100%' }}
                            value={selectedStudentId}
                            loading={studentsLoading}
                            showSearch={false}
                            onChange={(value: ID) => {
                              setHasQueried(false)
                              setSelectedRecordTypeFilter('all')
                              setSelectedStudentId(value)
                            }}
                          >
                            {students.map((student) => (
                              <Option key={student.id} value={student.id}>
                                {student.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        <div>
                          <div className="mb-2 text-sm font-medium text-slate-700">
                            月份
                          </div>
                          <DatePicker
                            picker="month"
                            format="YYYY-MM"
                            style={{ width: '100%' }}
                            value={selectedMonth}
                            inputReadOnly
                            onChange={handleMonthChange}
                          />
                        </div>
                        <div>
                          <div className="mb-2 text-sm font-medium text-slate-700">
                            日期范围
                          </div>
                          <RangePicker
                            format="YYYY-MM-DD"
                            style={{ width: '100%' }}
                            value={selectedDateRange}
                            presets={rangePresets}
                            inputReadOnly
                            disabled={!selectedMonth}
                            disabledDate={(current) => {
                              if (!selectedMonth || !current) {
                                return true
                              }
                              const start = selectedMonth.startOf('month')
                              const end = selectedMonth.endOf('month')
                              return (
                                current.isBefore(start, 'day') ||
                                current.isAfter(end, 'day')
                              )
                            }}
                            onChange={(dates) => {
                              if (!dates || !dates[0] || !dates[1]) {
                                setSelectedDateRange(null)
                                return
                              }
                              setSelectedDateRange([dates[0], dates[1]])
                            }}
                          />
                        </div>
                        <div>
                          <div className="mb-2 text-sm font-medium text-slate-700">
                            记录类型
                          </div>
                          <Select
                            style={{ width: '100%' }}
                            value={selectedRecordTypeFilter}
                            showSearch={false}
                            disabled={!hasQueried}
                            onChange={(value) => setSelectedRecordTypeFilter(value)}
                          >
                            <Option value="all">全部</Option>
                            <Option value="manual">手工记录</Option>
                            <Option value="makeup">补课记录</Option>
                            {batchFilterOptions.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {item.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      <div className="mt-3 space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="min-w-0 text-xs font-medium uppercase tracking-wide text-slate-500">
                            常用操作
                          </div>
                          <Button
                            type="primary"
                            ghost
                            className="!h-8 !rounded-md !px-3 !text-xs !font-medium"
                            disabled={!showGeneratePanel}
                            onClick={handleOpenGenerateDrawer}
                          >
                            生成配置
                          </Button>
                          <Button
                            type="dashed"
                            className="!h-8 !rounded-md !px-3 !text-xs !font-medium"
                            disabled={!canQuickCreate}
                            onClick={handleOpenQuickCreateModal}
                          >
                            手动记录
                          </Button>
                          <Button
                            danger
                            className="!h-8 !rounded-md !px-3 !text-xs !font-medium"
                            disabled={!canClearMonth}
                            onClick={handleClearCurrentMonth}
                          >
                            清空当月
                          </Button>
                          <Button
                            className="!h-8 !rounded-md !px-3 !text-xs !font-medium"
                            disabled={!showGeneratePanel}
                            onClick={() => setMonthPlanDrawerOpen(true)}
                          >
                            月配置
                          </Button>
                        </div>
                      </div>
                    </div>

                    {showRecordsTable ? (
                    <div
                      ref={tableContainerRef}
                      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-[0_18px_40px_-32px_rgba(15,23,42,0.3)]"
                    >
                      <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/70 px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <Tag className="m-0 rounded-full border-0 bg-white px-3 py-1 text-slate-600">
                            共 {filteredRecords.length} 条
                          </Tag>
                          <Tag className="m-0 rounded-full border-0 bg-white px-3 py-1 text-slate-600">
                            已选 {batchActionCount} 条
                          </Tag>
                          {batchActionCount > 0 ? (
                            <Tag className="m-0 rounded-full border-0 bg-white px-3 py-1 text-slate-600">
                              手工记录 {selectedManualIds.length} 条
                              {selectedGeneratedCount > 0
                                ? ` · 自动生成 ${selectedGeneratedCount} 条`
                                : ''}
                            </Tag>
                          ) : null}
                        </div>
                        <div
                          className={`flex flex-nowrap items-center gap-2 ${batchActionCount > 0 ? '' : 'invisible pointer-events-none'}`}
                          aria-hidden={batchActionCount === 0}
                        >
                          <Button
                            type="primary"
                            className="!h-8 !rounded-md !px-3 !text-xs !font-medium"
                            disabled={!canBatchEdit}
                            onClick={handleOpenBatchEdit}
                          >
                            批量编辑
                            {batchActionCount > 0 ? `(${batchActionCount})` : ''}
                          </Button>
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            className="!h-8 !rounded-md !px-3 !text-xs !font-medium"
                            disabled={!canBatchDelete}
                            loading={batchDeleteSaving}
                            onClick={handleBatchDelete}
                          >
                            批量删除
                            {selectedManualIds.length > 0
                              ? `(${selectedManualIds.length})`
                              : batchActionCount > 0
                                ? `(${batchActionCount})`
                                : ''}
                          </Button>
                        </div>
                      </div>
                      <Table
                        columns={columns}
                        dataSource={filteredRecords}
                        rowKey="id"
                        className="[-webkit-tap-highlight-color:transparent] [&_.ant-table-thead>tr>th]:bg-gray-50 [&_.ant-table-thead>tr>th]:text-gray-600 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-tbody>tr>td]:py-3 [&_.ant-table-tbody>tr.ant-table-row:hover>td]:bg-blue-50/40 [&_.ant-table-tbody>tr.ant-table-row-selected>td]:!bg-transparent [&_.ant-table-tbody>tr.ant-table-row-selected:hover>td]:!bg-transparent [&_.ant-table-tbody>tr:focus-within>td]:outline-none [&_.ant-table-selection-column]:px-2 [&_.ant-table-selection-column]:text-center"
                        rowSelection={rowSelection}
                        onRow={(record) => ({
                          onClick: (event) => {
                            const target = event.target as HTMLElement | null
                            if (
                              target?.closest(
                                'button, a, input, textarea, label, .ant-switch, .ant-input, .ant-input-affix-wrapper, .ant-select, .ant-picker, .ant-checkbox-wrapper, .ant-checkbox-input',
                              )
                            ) {
                              return
                            }
                            handleToggleRowSelection(record.id)
                          },
                        })}
                        rowClassName={(record) => {
                          const groupIndex =
                            dateGroupMeta.groupIndexMap[idKey(record.id)] ?? 0
                          const baseClass =
                            groupIndex % 2 === 0 ? 'bg-slate-50/40' : 'bg-white'
                          return `${baseClass} cursor-pointer`
                        }}
                        loading={loading}
                        pagination={false}
                        scroll={screens.md ? { y: tableScrollY } : undefined}
                        size={screens.md ? 'middle' : 'small'}
                      />
                    </div>
                  ) : hasQueried ? (
                    <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-20">
                      <Empty
                        description="当前筛选条件下暂无签到记录，可尝试切换记录类型或使用“手动记录 / 配置签到生成”。"
                      />
                    </div>
                  ) : (
                    <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-20">
                      <Empty description="选择学生后会自动加载当前月份的签到记录。" />
                    </div>
                  )}

                  </div>

                  {globalConfigDrawerOpen ? (
                    <Drawer
                      title="全局配置"
                      open={globalConfigDrawerOpen}
                      onClose={() => setGlobalConfigDrawerOpen(false)}
                      width={screens.md ? 560 : '100%'}
                      destroyOnHidden
                      extra={
                        <Button type="primary" loading={ruleSaving} onClick={handleSaveRules}>
                          保存规则
                        </Button>
                      }
                    >
                      <div className="space-y-4">
                        <div className="text-sm text-slate-500">
                          保留月数修改后会重算当前补课余额；默认单价和默认时长只影响后续新记录。
                        </div>
                        <div>
                          <div className="mb-2 text-sm font-medium text-slate-700">补课保留月数</div>
                          <InputNumber
                            min={1}
                            precision={0}
                            style={{ width: '100%' }}
                            value={ruleDraft.makeupRetentionMonths}
                            onChange={(value) =>
                              setRuleDraft((prev) => ({
                                ...prev,
                                makeupRetentionMonths: Number(value ?? 1),
                              }))
                            }
                          />
                        </div>
                        <div>
                          <div className="mb-2 text-sm font-medium text-slate-700">默认课时单价</div>
                          <InputNumber
                            min={0.01}
                            precision={2}
                            style={{ width: '100%' }}
                            value={ruleDraft.defaultCoursePrice}
                            onChange={(value) =>
                              setRuleDraft((prev) => ({
                                ...prev,
                                defaultCoursePrice: Number(value ?? 0),
                              }))
                            }
                          />
                        </div>
                        <div>
                          <div className="mb-2 text-sm font-medium text-slate-700">默认一节课分钟数</div>
                          <InputNumber
                            min={1}
                            precision={0}
                            style={{ width: '100%' }}
                            value={ruleDraft.defaultClassDurationMinutes}
                            onChange={(value) =>
                              setRuleDraft((prev) => ({
                                ...prev,
                                defaultClassDurationMinutes: Number(value ?? 1),
                              }))
                            }
                          />
                        </div>
                      </div>
                    </Drawer>
                  ) : null}

                  {batchEditModalOpen ? (
        <Suspense fallback={null}>
          <BatchEditModal
                        open={batchEditModalOpen}
                        selectedCount={batchSelectedIds.length}
                        teacherOptions={teachers}
                        teachersLoading={teachersLoading}
                        attendanceMode={batchEditAttendanceMode}
                        note={batchEditNote}
                        teacherValue={batchEditTeacherValue}
                        confirmLoading={batchEditSaving}
                        onCancel={() => setBatchEditModalOpen(false)}
                        onOk={handleSubmitBatchEdit}
                        onAttendanceModeChange={setBatchEditAttendanceMode}
                        onTeacherValueChange={setBatchEditTeacherValue}
                        onNoteChange={setBatchEditNote}
                      />
                    </Suspense>
                  ) : null}

                  {quickCreateModalOpen ? (
                    <Suspense fallback={null}>
                      <QuickCreateModal
                        open={quickCreateModalOpen}
                        date={quickCreateDate}
                        items={quickCreateItems}
                        teachers={teachers}
                        teachersLoading={teachersLoading}
                        saving={quickCreateSaving}
                        recordKind={quickCreateRecordKind}
                        width={screens.md ? 900 : '100%'}
                        onCancel={() => setQuickCreateModalOpen(false)}
                        onOk={handleSubmitQuickCreate}
                        onDateChange={setQuickCreateDate}
                        onRecordKindChange={handleQuickCreateRecordKindChange}
                        onItemChange={handleQuickCreateItemChange}
                        onAddItem={handleQuickCreateAddItem}
                        onDuplicateItem={handleQuickCreateDuplicateItem}
                        onRemoveItem={handleQuickCreateRemoveItem}
                      />
                    </Suspense>
                  ) : null}

                  {generateDrawerOpen ? (
                    <Suspense fallback={null}>
                      <GenerateRecordsDrawer
                        open={generateDrawerOpen}
                        width={screens.md ? 900 : '100%'}
                        currentConfig={currentConfig}
                        generating={generating}
                        selectedMonth={selectedMonth}
                        generateRange={generateRange}
                        rangePresets={rangePresets}
                        onClose={() => setGenerateDrawerOpen(false)}
                        onGenerate={() => handleGenerate()}
                        onGenerateRangeChange={setGenerateRange}
                      />
                    </Suspense>
                  ) : null}

                  {classConfigDrawerOpen ? (
                    <Suspense fallback={null}>
                      <ClassConfigDrawer
                        open={classConfigDrawerOpen}
                        width={screens.md ? 900 : '100%'}
                        currentConfig={currentConfig}
                        isConfigDirty={isConfigDirty}
                        savingCurrentConfig={savingCurrentConfig}
                        weekdays={configDraftWeekdays}
                        isAllDayPricing={configDraftIsAllDayPricing}
                        monthlyPrice={configDraftMonthlyPrice}
                        templates={configDraftTemplates}
                        teachers={teachers}
                        teachersLoading={teachersLoading}
                        onClose={() => setClassConfigDrawerOpen(false)}
                        onSaveCurrentConfig={handleSaveCurrentConfig}
                        onAllDayPricingChange={(checked) => {
                          setConfigDraftIsAllDayPricing(checked)
                          if (!checked) {
                            setConfigDraftMonthlyPrice(undefined)
                          }
                        }}
                        onMonthlyPriceChange={setConfigDraftMonthlyPrice}
                        onWeekdaysChange={setConfigDraftWeekdays}
                        onTemplateChange={handleConfigDraftTemplateChange}
                        onAddTemplate={handleConfigDraftAddTemplate}
                        onRemoveTemplate={handleConfigDraftRemoveTemplate}
                      />
                    </Suspense>
                  ) : null}

                  <MonthPlanDrawer
                    open={monthPlanDrawerOpen}
                    width={screens.lg ? 600 : screens.md ? 860 : '100%'}
                    summary={summaryData}
                    hasSelection={Boolean(selectedStudentId && monthText)}
                    defaultClassDurationMinutes={rules?.defaultClassDurationMinutes ?? 40}
                    loading={loading}
                    monthPlan={monthPlan}
                    onClose={() => setMonthPlanDrawerOpen(false)}
                  />
                </>
              ),
            },
            {
              key: 'versions',
              label: '配置管理',
              children: (
                <Card
                  extra={
                    <Space>
                      <Button
                        className="!h-9 !rounded-xl !px-4 !font-medium"
                        onClick={handleOpenClassConfigDrawer}
                      >
                        上课配置
                      </Button>
                      <Button
                        className="!h-9 !rounded-xl !px-4 !font-medium"
                        onClick={() => setGlobalConfigDrawerOpen(true)}
                      >
                        全局配置
                      </Button>
                    </Space>
                  }
                >
                  <div className="flex flex-col gap-3">
                    {configVersions.length > 0 ? (
                      configVersions.map((version) => (
                        <div
                          key={version.id}
                          className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-[0_8px_24px_-20px_rgba(15,23,42,0.18)]"
                        >
                          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="min-w-0">
                              <div className="text-xs text-slate-500">创建时间</div>
                              <div className="mt-1 text-sm font-medium text-slate-900">
                                {dayjs(version.createdAt).format('YYYY-MM-DD HH:mm')}
                              </div>
                            </div>
                            <Tag
                              color={version.isCurrent ? 'green' : 'default'}
                              className="w-fit"
                            >
                              {version.isCurrent ? '当前' : '历史'}
                            </Tag>
                            <Space size={0} wrap>
                              <Button
                                type="link"
                                onClick={() => setSelectedVersionBatchDrawerId(version.id)}
                              >
                                查看详情
                              </Button>
                              <Button
                                type="link"
                                disabled={version.isCurrent || !selectedStudentId}
                                onClick={() => handleSetCurrentVersion(version)}
                              >
                                设为当前
                              </Button>
                              <Button
                                type="link"
                                danger
                                disabled={!version.canDelete}
                                loading={deletingVersionId === version.id}
                                onClick={() => handleDeleteConfigVersion(version)}
                              >
                                删除
                              </Button>
                            </Space>
                          </div>
                        </div>
                      ))
                    ) : (
                      <Empty description="暂无配置版本" />
                    )}
                  </div>
                </Card>
              ),
            },
          ]}
        />

      <Drawer
        title={
          selectedVersionBatchDrawer
            ? `配置版本 ${dayjs(selectedVersionBatchDrawer.createdAt).format('YYYY-MM-DD HH:mm')} 的详情`
            : '配置详情'
        }
        open={selectedVersionBatchDrawerId !== null}
        width={screens.lg ? 860 : screens.md ? 720 : '100%'}
        destroyOnHidden
        onClose={() => setSelectedVersionBatchDrawerId(null)}
      >
        {selectedVersionBatchDrawer ? (
          <div className="flex flex-col gap-4">
            <ConfigDetailSections
              weekdays={selectedVersionBatchDrawer.weekdays}
              isAllDayPricing={selectedVersionBatchDrawer.isAllDayPricing}
              monthlyPrice={selectedVersionBatchDrawer.monthlyPrice}
              templates={selectedVersionBatchDrawer.templates}
            />

            {selectedVersionBatchDrawer.generationBatches.length ? (
              <Card size="small" title={`生成批次（共 ${selectedVersionBatchDrawer.generationBatchCount} 个）`}>
                <Table
                  columns={versionBatchColumns}
                  dataSource={selectedVersionBatchDrawer.generationBatches}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 520 }}
                />
              </Card>
            ) : (
              <Card size="small" title={`生成批次（共 ${selectedVersionBatchDrawer.generationBatchCount} 个）`}>
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  该配置版本暂无生成批次
                </div>
              </Card>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            该配置版本暂无生成批次
          </div>
        )}
      </Drawer>

      </Card>
    </div>
  )
}

export default ClassRecordsPage
